/**
 * Sync Telemetry Utilities
 *
 * Structured logging for reconnection events following the Edge Function pattern.
 * Provides centralized, consistent telemetry with automatic PII masking and message truncation.
 *
 * @module syncTelemetry
 */

import type { ProviderKey, SyncInitReason } from '@/types/sync';
import { maskUserId } from './piiMasking';

/**
 * Maximum length for error details to prevent logging secrets.
 * @constant
 * @internal Exported for tests only, not part of public API
 */
export const MAX_DETAILS_LENGTH = 100;

/**
 * Number of characters to show from correlation ID for readability.
 * @constant
 * @internal Exported for tests only, not part of public API
 */
export const CORRELATION_ID_DISPLAY_LENGTH = 8;

/**
 * Prefix for filtering reconnection logs in console.
 * @constant
 * @internal Exported for tests only, not part of public API
 */
export const LOG_PREFIX = '[Reconnection]';

/**
 * Reconnection event types tracked in telemetry.
 *
 * - **shown**: Modal displayed to user
 * - **clicked**: User clicked reconnect button
 * - **success**: Reconnection completed successfully
 * - **fail**: Reconnection failed
 * - **snoozed**: User dismissed/snoozed the modal
 */
export type ReconnectionEvent = 'shown' | 'clicked' | 'success' | 'fail' | 'snoozed';

/**
 * Metadata for a reconnection event.
 *
 * All PII fields (userId) will be automatically masked in log output.
 * Details field will be truncated to MAX_DETAILS_LENGTH.
 */
export interface ReconnectionEventMetadata {
  /** Type of reconnection event */
  event: ReconnectionEvent;
  /** Provider key (e.g., 'google-drive') */
  provider: ProviderKey;
  /** Reason for sync initialization */
  reason: SyncInitReason;
  /** User ID (will be masked to first 8 chars + '...') */
  userId: string;
  /** Correlation ID for tracking related events */
  correlationId: string;
  /** Retry delay in milliseconds (for 'snoozed' events) */
  retryAfterMs?: number;
  /** Event duration in milliseconds (for 'success'/'fail' events) */
  durationMs?: number;
  /** Additional error details (will be truncated to MAX_DETAILS_LENGTH) */
  details?: string;
}

/**
 * Truncates a string to a maximum length with optional suffix.
 *
 * General-purpose truncation utility to prevent unbounded string logging.
 * Use for details, error messages, or any user-provided text that needs bounds.
 *
 * @param str - The string to truncate
 * @param maxLength - Maximum length (inclusive)
 * @param suffix - Optional suffix to append (default: '')
 * @returns Truncated string with suffix, or empty string if input is invalid
 *
 * @example
 * ```typescript
 * truncate('A very long error message that exceeds limit', 20)
 * // Returns: 'A very long error me'
 *
 * truncate('Short', 20)
 * // Returns: 'Short'
 *
 * truncate('', 20)
 * // Returns: ''
 * ```
 */
export function truncate(str: string, maxLength: number, suffix: string = ''): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  if (str.length <= maxLength) {
    return str;
  }

  return str.substring(0, maxLength) + suffix;
}

/**
 * Truncates an ID to first N characters followed by ellipsis.
 *
 * Used for shortening correlation IDs in logs while maintaining correlation capability.
 * Wraps `truncate()` with ellipsis suffix for readability.
 *
 * @param id - The ID to truncate
 * @param length - Number of characters to keep (default: CORRELATION_ID_DISPLAY_LENGTH)
 * @returns Truncated ID with '...' suffix, or empty string if input is invalid
 *
 * @example
 * ```typescript
 * shortId('1a2b3c4d-5e6f-7a8b-9c0d-1e2f3a4b5c6d', 8)
 * // Returns: '1a2b3c4d...'
 *
 * shortId('short', 8)
 * // Returns: 'short...'
 *
 * shortId('', 8)
 * // Returns: ''
 * ```
 */
export function shortId(id: string, length: number = CORRELATION_ID_DISPLAY_LENGTH): string {
  if (!id || typeof id !== 'string') {
    return '';
  }

  // For IDs shorter than length, still add ellipsis for consistency
  if (id.length <= length) {
    return id.length > 0 ? `${id}...` : '';
  }

  return truncate(id, length, '...');
}

/**
 * Logs a reconnection event with structured telemetry following Edge Function pattern.
 *
 * **PII Protection**:
 * - userId is automatically masked using maskUserId()
 * - correlationId is truncated to first 8 chars
 * - details field is truncated to MAX_DETAILS_LENGTH
 *
 * **Output Format**:
 * - JSON structured log entry
 * - Uses console.log for info/success events
 * - Uses console.error for 'fail' events
 * - Includes LOG_PREFIX for filtering
 *
 * @param metadata - Reconnection event metadata
 *
 * @example
 * ```typescript
 * logReconnectionEvent({
 *   event: 'shown',
 *   provider: 'google-drive',
 *   reason: 'not-connected',
 *   userId: '1a2b3c4d-5e6f-7a8b-9c0d-1e2f3a4b5c6d',
 *   correlationId: 'abc123-def456-ghi789',
 * });
 * // Console output:
 * // [Reconnection] {"event":"shown","provider":"google-drive","reason":"not-connected","user_id":"1a2b3c4d...","correlation_id":"abc123..."}
 * ```
 */
export function logReconnectionEvent(metadata: ReconnectionEventMetadata): void {
  // Build structured log entry
  const logEntry: Record<string, unknown> = {
    event: metadata.event,
    provider: metadata.provider,
    reason: metadata.reason,
    user_id: maskUserId(metadata.userId),
    correlation_id: shortId(metadata.correlationId, CORRELATION_ID_DISPLAY_LENGTH),
  };

  // Add optional fields
  if (metadata.retryAfterMs !== undefined) {
    logEntry['retry_after_ms'] = metadata.retryAfterMs;
  }

  if (metadata.durationMs !== undefined) {
    logEntry['ms'] = metadata.durationMs;
  }

  if (metadata.details !== undefined && metadata.details !== null) {
    logEntry['details'] = truncate(metadata.details, MAX_DETAILS_LENGTH);
  }

  // Output JSON with prefix
  const logOutput = `${LOG_PREFIX} ${JSON.stringify(logEntry)}`;

  // Use console.error for failures, console.log for everything else
  if (metadata.event === 'fail') {
    console.error(logOutput);
  } else {
    console.log(logOutput);
  }
}
